import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { PublicSubscriptionCollectionItemResponse } from '../response/public-subscription-collection-item.response';
import { SubscriptionNumber } from '../../../shared/types';
import { PublicSubscriptionDetailsResponse } from '../response/public-subscription-details.response';
import { PublicSubscriptionOrderCollectionItemResponse } from '../response/public-subscription-order-collection-item.response';
import { PublicSubscriptionBillingAddressResponse } from '../response/public-subscription-billing-address.response';
import { PublicSubscriptionBillingAddressUpdateRequest } from '../requests/public-subscription-billing-address-update.request';
import { PublicSubscriptionShippingAddressUpdateRequest } from '../requests/public-subscription-shipping-address-update.request';
import { PublicSubscriptionShippingAddressResponse } from '../response/public-subscription-shipping-address.response';
import { PublicSubscriptionShippingMethodCollectionItemResponse } from '../response';
import { PublicSubscriptionUpdateRequest } from '../requests/public-subscription-update.request';

@Injectable({
  providedIn: 'root',
})
export class PublicSubscriptionApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    cacheable?: boolean;
    request: CollectionRequest;
  }): Observable<CollectionResponse<PublicSubscriptionCollectionItemResponse>> {
    const path = `subscription`;
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicSubscriptionCollectionItemResponse,
          ),
        ),
      );
  }

  public cancelByNumber(number: SubscriptionNumber): Observable<void> {
    const path = `subscription/${HttpParams.Number}/cancel`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public suspendByNumber(number: SubscriptionNumber): Observable<void> {
    const path = `subscription/${HttpParams.Number}/suspend`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public resumeByNumber(number: SubscriptionNumber): Observable<void> {
    const path = `subscription/${HttpParams.Number}/resume`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchDetailsByNumber(options: {
    number: SubscriptionNumber;
  }): Observable<PublicSubscriptionDetailsResponse> {
    const { number } = options;
    const path = `subscription/${HttpParams.Number}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Number]: number,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicSubscriptionDetailsResponse,
          ),
        ),
      );
  }

  public fetchAvailableShippingMethodCollection(params: {
    cacheable?: boolean;
    countryId?: Nillable<Guid>;
    subscriptionNumber: SubscriptionNumber;
    skipByLoading: boolean;
    request: CollectionRequest;
  }): Observable<
    CollectionResponse<PublicSubscriptionShippingMethodCollectionItemResponse>
  > {
    const path = `subscription/${HttpParams.Number}/shipping/method`;
    let queryParams = this.provider.httpHelper.collectionRequestToQueryParams(
      params.request,
    );

    if (!isNil(params.countryId)) {
      queryParams = { ...queryParams, countryId: params.countryId };
    }

    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(queryParams)
        .params({
          [HttpParams.Number]: params.subscriptionNumber,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(params?.cacheable, false),
      )
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(params?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicSubscriptionShippingMethodCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchSubscriptionOrderCollection(params: {
    request: CollectionRequest;
    number: SubscriptionNumber;
  }): Observable<
    CollectionResponse<PublicSubscriptionOrderCollectionItemResponse>
  > {
    const path = `subscription/${HttpParams.Number}/order`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.Number]: params.number })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(
            params.request,
          ),
        )
        .build(),
    );

    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicSubscriptionOrderCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchBillingAddressDetails(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionBillingAddressResponse> {
    const path = `subscription/${HttpParams.Number}/billing`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicSubscriptionBillingAddressResponse,
          ),
        ),
      );
  }

  public updateBillingAddress(param: {
    number: SubscriptionNumber;
    request: PublicSubscriptionBillingAddressUpdateRequest;
  }): Observable<void> {
    const { number, request } = param;
    const path = `subscription/${HttpParams.Number}/billing`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      PublicSubscriptionBillingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public updateSubscription(param: {
    number: SubscriptionNumber;
    request: PublicSubscriptionUpdateRequest;
  }): Observable<void> {
    const { number, request } = param;
    const path = `subscription/${HttpParams.Number}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      PublicSubscriptionUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public updateShippingAddress(param: {
    number: SubscriptionNumber;
    request: PublicSubscriptionShippingAddressUpdateRequest;
  }): Observable<void> {
    const { number, request } = param;
    const path = `subscription/${HttpParams.Number}/shipping`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      PublicSubscriptionShippingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchShippingAddressDetails(
    number: SubscriptionNumber,
  ): Observable<PublicSubscriptionShippingAddressResponse> {
    const path = `subscription/${HttpParams.Number}/shipping`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicSubscriptionShippingAddressResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
